import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Gift, Check, Send, BellRing } from "lucide-react";
import { toast } from "sonner";

const STATUS_COLORS = {
  pending: "bg-yellow-100 text-yellow-800",
  approved: "bg-blue-100 text-blue-800",
  gift_card_sent: "bg-emerald-100 text-emerald-800",
};

const money = (n) => `$${Number(n || 0).toFixed(2)}`;

/**
 * Iter43 Phase 3 — Rewards Inbox.
 * Owner-only view for pending / approved / gift-card-sent referral rewards.
 * Provides manual "Approve" + "Mark as sent" actions until a live gift-card
 * provider is added in Phase 4.
 */
export default function Rewards() {
  const [rewards, setRewards] = useState([]);
  const [notifs, setNotifs] = useState([]);
  const [customers, setCustomers] = useState({});
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState("pending");
  const [busy, setBusy] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const [rw, nt, cs] = await Promise.all([
        api.get(filter === "all" ? "/rewards" : `/rewards?status=${filter}`),
        api.get("/notifications"),
        api.get("/customers"),
      ]);
      setRewards(rw.data || []);
      setNotifs(nt.data || []);
      setCustomers((cs.data || []).reduce((m, c) => { m[c.id] = c; return m; }, {}));
    } catch { toast.error("Failed to load rewards"); }
    setLoading(false);
  };

  useEffect(() => { load(); }, [filter]); // eslint-disable-line

  const doApprove = async (id) => {
    setBusy(true);
    try {
      await api.post(`/rewards/${id}/approve`, { notes: "" });
      toast.success("Reward approved");
      load();
    } catch (e) { toast.error(e.response?.data?.detail || "Approve failed"); }
    finally { setBusy(false); }
  };
  const markSent = async (id) => {
    setBusy(true);
    try {
      await api.post(`/rewards/${id}/mark-sent`, { notes: "Manually fulfilled" });
      toast.success("Marked as sent");
      load();
    } catch (e) { toast.error(e.response?.data?.detail || "Update failed"); }
    finally { setBusy(false); }
  };
  const markNotifRead = async (id) => {
    try { await api.post(`/notifications/${id}/read`); load(); } catch { /* silent */ }
  };

  const unread = notifs.filter((n) => !n.read);

  return (
    <div className="p-6 max-w-6xl mx-auto" data-testid="rewards-page">
      <div className="flex items-center gap-3 mb-6">
        <Gift className="w-6 h-6 text-[#FF5A00]"/>
        <h1 className="font-heading text-3xl font-bold">Referral rewards</h1>
      </div>

      {unread.length > 0 && (
        <div className="bg-orange-50 border border-orange-200 rounded-lg p-4 mb-4" data-testid="notifications-block">
          <div className="flex items-center gap-2 mb-2">
            <BellRing className="w-4 h-4 text-[#FF5A00]"/>
            <div className="font-medium text-sm">{unread.length} pending owner notification{unread.length === 1 ? "" : "s"}</div>
          </div>
          {unread.slice(0, 5).map((n) => (
            <div
              key={n.id}
              className="flex items-center justify-between text-sm py-1"
              data-testid={`notification-${n.id}`}
            >
              <div>
                <div className="font-medium">{n.title}</div>
                <div className="text-xs text-zinc-500">{n.body}</div>
              </div>
              <Button size="sm" variant="outline" onClick={() => markNotifRead(n.id)}>
                Mark read
              </Button>
            </div>
          ))}
        </div>
      )}

      <div className="flex items-center gap-2 mb-4">
        {["pending", "approved", "gift_card_sent", "all"].map((f) => (
          <button
            key={f}
            onClick={() => setFilter(f)}
            className={`text-xs uppercase font-mono-accent tracking-widest px-3 py-1.5 rounded ${filter === f ? "bg-zinc-900 text-white" : "bg-zinc-100 text-zinc-600 hover:bg-zinc-200"}`}
            data-testid={`rewards-filter-${f}`}
          >
            {f.replace(/_/g, " ")}
          </button>
        ))}
      </div>

      {loading && <div className="text-sm text-zinc-500">Loading…</div>}

      {!loading && rewards.length === 0 && (
        <div className="text-sm text-zinc-500 py-8 text-center" data-testid="rewards-empty">
          No {filter === "all" ? "" : filter.replace(/_/g, " ") + " "}rewards yet.
        </div>
      )}

      <div className="grid gap-3">
        {rewards.map((r) => {
          const c = customers[r.referrer_customer_id];
          return (
            <div
              key={r.id}
              className="bg-white border border-zinc-200 rounded-lg p-4 card-crisp flex flex-wrap items-center gap-4"
              data-testid={`reward-row-${r.id}`}
            >
              <div className="font-mono-accent text-2xl font-bold text-emerald-700">{money(r.amount)}</div>
              <div className="flex-1 min-w-0">
                <div className="font-medium">
                  {c ? c.name : "—"}
                  {c?.phone && <span className="text-xs text-zinc-500 ml-2">{c.phone}</span>}
                </div>
                <div className="text-xs text-zinc-500 mt-1">
                  Created {new Date(r.created_at).toLocaleDateString()} · Invoice {r.invoice_id?.slice(-6)}
                </div>
              </div>
              <span className={`text-[10px] uppercase font-mono-accent tracking-widest px-2 py-1 rounded ${STATUS_COLORS[r.status] || "bg-zinc-100"}`}>
                {r.status.replace(/_/g, " ")}
              </span>
              {r.status === "pending" && (
                <>
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={() => doApprove(r.id)}
                    disabled={busy}
                    data-testid={`reward-approve-${r.id}`}
                  >
                    <Check className="w-3 h-3 mr-1"/> Approve
                  </Button>
                  <Button
                    size="sm"
                    className="bg-emerald-600 hover:bg-emerald-700 text-white"
                    onClick={() => markSent(r.id)}
                    disabled={busy}
                    data-testid={`reward-mark-sent-${r.id}`}
                  >
                    <Send className="w-3 h-3 mr-1"/> Mark as sent
                  </Button>
                </>
              )}
              {r.status === "approved" && (
                <Button
                  size="sm"
                  className="bg-emerald-600 hover:bg-emerald-700 text-white"
                  onClick={() => markSent(r.id)}
                  disabled={busy}
                  data-testid={`reward-mark-sent-${r.id}`}
                >
                  <Send className="w-3 h-3 mr-1"/> Mark gift card sent
                </Button>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}
